import { formatPriceIntl } from "../../../../common-deprecated/priceUtils";
import type { CommonSettingsType } from "../../../../common-deprecated/settings/fetchCommonSettings";
import type { AvailableGradesForConfigurableGradeType } from "../../../shared-logic/types/AvailableInOtherGradesType";
import { getCarImage } from "../../shared-logic/utils/imageUtils";
import type { ImageCarConfigType, ImageCarType } from "../../../../common-deprecated/settings/utils/ccisSettingUtils";
import { AvailableInOtherGradeModalType, CarConfigConfigureSection } from "./types/SectionTypes";

type availableInOtherGradesDataType = { name: string; shortId: string };

/**
 * Method which checks if the provided items are kept or lost when changing grades.
 *
 * @param availableGrade - The available grade that is currently selected.
 * @param data - The items that needs to be checked whether they are kept or lost.
 * Note that you can pass multiple ids when checking the motorization. Both engine & transmission need to be checked this case.
 *
 * @returns The provided data, categorized by the items that are kept and the ones that will be lost upon changing to the 'availble in other grade'.
 */
export const checkKeptAndLostItemsWhenChangingGrade = (
    availableGrade: AvailableGradesForConfigurableGradeType,
    data: { ids: string[]; type: "motorization" | "exteriorColour" | "upholstery" | "wheel"; name: string }[],
): { kept: availableInOtherGradesDataType[]; lost: availableInOtherGradesDataType[] } => {
    const kept: availableInOtherGradesDataType[] = [];
    const lost: availableInOtherGradesDataType[] = [];

    data.forEach((item) => {
        const formattedItem: availableInOtherGradesDataType = { name: item.name, shortId: item.ids[0] };

        // To check the motorization, both the transmission and engine id needs to be matched.
        const itemIdsToCompare =
            item.type === "motorization"
                ? [availableGrade?.motorization.engine.id, availableGrade?.motorization.transmission.id]
                : [availableGrade?.[item.type].id];

        if (item.ids.every((id) => itemIdsToCompare.find((itemIdToCompare) => itemIdToCompare === id)))
            kept.push(formattedItem);
        else lost.push(formattedItem);
    });

    return { kept, lost };
};

export const calculateTotalPrice = (
    commonSettings: CommonSettingsType,
    availableGrade: AvailableGradesForConfigurableGradeType,
    format: "cash" | "monthly",
): string | undefined => {
    const totalPrice =
        availableGrade.car.priceInfo?.[format] +
        availableGrade.exteriorColour.priceInfo?.[format] +
        availableGrade.upholstery.priceInfo?.[format] +
        availableGrade.wheel.priceInfo?.[format];

    if (totalPrice === 0) return undefined;
    return formatPriceIntl(commonSettings, totalPrice).primaryPrice;
};

/**
 * Override the image options from CCIS by the available in other grades one.
 *
 * @param carImageInput - The original CCIS config.
 * @param selectedGrade - The currently selected grade of AvailableGradesForConfigurableGradeType.
 *
 * @returns ImageCarConfigType - The modified CCIS config or the original one if there is no grade selected.
 */
export const overrideCCISImageConfig = (
    carImageInput: ImageCarConfigType,
    selectedGrade: AvailableGradesForConfigurableGradeType | undefined,
): ImageCarConfigType => {
    if (!selectedGrade) return carImageInput;

    // Always clear extras & packs, these are always removed when changing to another grade.
    carImageInput.accessoryIds?.splice(0);
    carImageInput.optionIds?.splice(0);
    carImageInput.packIds?.splice(0);

    // Don't add the wheels to the CCIS image if the wheels are default for that grade. This would break the image.
    if (selectedGrade.wheel.standard === false) {
        const type = selectedGrade.wheel.type === "accessory" ? "accessoryIds" : "optionIds";
        carImageInput[type] = [selectedGrade.wheel.shortId];
    }

    carImageInput.colorCode = selectedGrade.exteriorColour.internalCode;
    carImageInput.upholsteryCode = selectedGrade.upholstery.internalCode;

    return carImageInput;
};

export const getVisual = (
    frame: number,
    selectedGrade: AvailableGradesForConfigurableGradeType | undefined,
    modelId: string,
    commonSettings: CommonSettingsType,
    modifiedCarImageInput: ImageCarConfigType,
):
    | {
          url: string;
          carIds: ImageCarType;
      }
    | undefined => {
    if (!selectedGrade) return undefined;
    const carIds: ImageCarType = { carId: selectedGrade.car.id, modelId };
    const carImage = getCarImage(
        commonSettings,
        frame,
        carIds,
        { type: "webp", width: 1000, height: 450 },
        modifiedCarImageInput,
    ).src;
    return { url: carImage, carIds };
};

export const getCarConfigSectionFromAvailableInOtherGrades = (
    section: AvailableInOtherGradeModalType,
): { carConfigSection: CarConfigConfigureSection; sectionString: "engine" | "colour" | "wheels" | "upholstery" } => {
    switch (section) {
        case AvailableInOtherGradeModalType.Engine:
            return { carConfigSection: CarConfigConfigureSection.Engine, sectionString: "engine" };
        case AvailableInOtherGradeModalType.Colour:
            return { carConfigSection: CarConfigConfigureSection.Colour, sectionString: "colour" };
        case AvailableInOtherGradeModalType.Wheel:
            return { carConfigSection: CarConfigConfigureSection.Wheels, sectionString: "wheels" };
        default:
            return { carConfigSection: CarConfigConfigureSection.Upholstery, sectionString: "upholstery" };
    }
};
